import { Navigate, useLocation } from "react-router-dom";
import { JSX, useEffect } from "react";
import { redactEmails, redactGiftTokens } from "@/features/activityLog/lib/sanitize";
import { track } from "@/shared/lib/analytics";
import { getRouteLogin } from "@/shared/config/router";
import { SUPPORTED_LANG } from "@/shared/config/localstorage";
import { useUser } from "@/shared/lib/hooks/useUser/useUser";
import { isAppOnlyPath } from "@/features/accountNavV5/model/config";

interface RequireAuthProps {
    children: JSX.Element;
}

/**
 * GA4 `login_required`: a page asked for sign-in. Only its path, cleaned like
 * page_location (no query: it can carry ?q= or a token). routePattern would
 * pull routeConfig in here and close an import cycle through the router.
 */
const LoginRequiredBeacon = ({ pathname, lang }: { pathname: string; lang?: string }) => {
    useEffect(() => {
        track("login_required", { from_path: redactGiftTokens(redactEmails(pathname)), site_lang: lang });
    }, [pathname, lang]);
    return null;
};

export function RequireAuth({ children }: RequireAuthProps) {
    const { user } = useUser();
    const location = useLocation();

    // /account/training и /account/calendar — предпросмотр нового меню для
    // админов (features/accountNavV5), пока рубильник SITE_WORKOUT_PAGES_HIDDEN
    // не в "all". Раньше этих адресов не было вовсе, и невошедший получал голое
    // «404» маршрута NOT_FOUND, а не вход — так и остаётся: без входа админом не
    // быть. Раньше всего остального — и адрес возврата после входа не пишется.
    // В "all" «Тренировки» открыты всем (isAppOnlyPath их не называет): вход и
    // возврат на них — как у любой страницы кабинета.
    if (!user && isAppOnlyPath(location.pathname)) {
        return <div>404</div>;
    }

    // A one-time login code from the app or the watch (?code=, see
    // features/auth/lib/webHandoff.ts) is not handled here: WebHandoffGate
    // (App.tsx) redeems it for every page before this renders at all, so by
    // now it is gone — nothing here redeems it again, bounces to /login while
    // it is being exchanged, or stores a return path with it in.

    // #418/#979: persist return path in sessionStorage AND as ?from= URL
    // param — ONLY when the user is not yet authenticated. The old
    // unconditional setItem ran on every render including AFTER login,
    // so the post-login removeItem in LoginPage/GoogleCallbackPage was
    // immediately overwritten and the key stayed in sessionStorage
    // (which is what the tester observed in #979 step 5).
    if (!user) {
        sessionStorage.setItem("auth_return_url", location.pathname + location.search);
    }

    // Post-Stripe redirects land unauthenticated. The course page itself runs
    // claim/auto-login on ?payment_success=true&session_id=…; let the page
    // render here so AccountPage chrome stays and useCoursePaymentSuccess
    // fires. Without this, the buyer bounces to /login with a valid claim
    // token waiting at the server.
    const sp = new URLSearchParams(location.search);
    const isPostPayment =
        sp.get("payment_success") === "true" && !!sp.get("session_id");
    let hasPendingClaim = false;
    try {
        hasPendingClaim = !!sessionStorage.getItem("course_purchase_success_session");
    } catch {}
    if (!user && (isPostPayment || hasPendingClaim)) {
        return children;
    }

    if (!user) {
        // The login page in the language of the page asked for (an expired
        // code from an English watch must not land on the Russian login);
        // getRouteLogin's own default for an address without one.
        const maybeLang = location.pathname.split("/")[1];
        const lang = SUPPORTED_LANG.includes(maybeLang) ? maybeLang : undefined;
        const returnPath = location.pathname + location.search;
        const loginUrl = `${getRouteLogin(lang)}?from=${encodeURIComponent(returnPath)}`;
        return (
            <>
                <LoginRequiredBeacon pathname={location.pathname} lang={lang} />
                <Navigate to={loginUrl} state={{ from: location }} replace />
            </>
        );
    }

    return children;
}
